/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import styled from '@emotion/styled';
import * as font from '@constant/font';
import * as color from '@constant/color';
import * as size from '@constant/size';
import * as design from '@constant/design';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import Gap from '@component/common/gap';
import TextBlock from 'component/common/block/text-block';
import ImageTextBlock from 'component/common/block/image-text-block';
import AuthBox from 'component/auth/auth-box';
import TextInput from '@component/common/input/text-input';
import { createAuthLogoImage } from '@util/image';
import { createErrorText } from '@util/string';
import { useAuthenticationCode, useAuthInput, useFindMe, useSignout } from '@hook/auth/useAuth';
import { SERVICE } from '@config/environment';

const Signout: React.FC = () => {
  const [isRemoved, setIsRemoved] = useState<boolean>(false);
  const me = useFindMe();

  const email = me?.data.email ?? '';
  const errorCode = useTypedSelector((state) => state.rootReducer?.authReducer?.errorCode) ?? null;

  const { code } = useAuthInput();
  const { removeMe } = useSignout();
  const { authenticateCode, issueAuthenticationCode, codeState } = useAuthenticationCode();

  const handleCodeSendButtonClick = async () => await issueAuthenticationCode({ service: SERVICE, email: email });
  const handleCodeAuthenticateButtonClick = async () => authenticateCode(email, code.value);
  const handleSignoutButtonClick = async () => {
    const result = await removeMe();
    if (!result) return;
    setIsRemoved(true);
  };

  return (
    <AuthBox design={design.AUTH_BOX_DESIGN}>
      <ImageTextBlock
        src={createAuthLogoImage()}
        alt='otterbrick'
        text='회원탈퇴'
        design={design.AUTH_LOGO_DESIGN}
      />
      <Gap height={size.MARGIN_2} />
      {codeState === 'init' && (
        <>
          <TextBlock design={design.AUTH_TEXT_DESIGN} text={email} />
          <TextBlock
            text={createErrorText(errorCode)}
            design={{
              ...design.ERROR_TEXT_DESIGN,
              box: { ...design.ERROR_TEXT_DESIGN, height: size.ERROR_TEXT_HEIGHT_1 },
            }}
          />
          <TextBlock
            text={'인증번호 보내기'}
            design={design.createAuthButtonDesign()}
            click={{ handler: handleCodeSendButtonClick, mode: 'throttle', delay: 3000 }}
          />
        </>
      )}
      {codeState === 'sent' && (
        <>
          <TextBlock
            design={{
              text: { size: 'medium' },
              box: { width: size.AUTH_BLOCK_WIDTH, height: size.AUTH_BLOCK_HEIGHT },
            }}
            text={email}
          />
          <TextInput
            name={'code'}
            placeholder={'인증번호'}
            design={{ ...design.AUTH_INPUT_DESIGN, box: { ...design.AUTH_INPUT_DESIGN.box, alignX: 'center' } }}
            onChange={code.handler}
            value={code.value}
          />
          <TextBlock
            text={createErrorText(errorCode)}
            design={{
              ...design.ERROR_TEXT_DESIGN,
              box: { ...design.ERROR_TEXT_DESIGN, height: size.ERROR_TEXT_HEIGHT_1 },
            }}
          />
          <TextBlock
            text={'인증하기'}
            design={design.createAuthButtonDesign()}
            click={{ handler: handleCodeAuthenticateButtonClick, mode: 'throttle', delay: 3000 }}
          />
          <Gap height={size.MARGIN_1} />
          <TextBlock
            text={'인증번호 다시 보내기'}
            design={design.createAuthButtonDesign()}
            click={{ handler: handleCodeSendButtonClick, mode: 'throttle', delay: 3000 }}
          />
        </>
      )}
      {codeState === 'authenticated' && (
        <>
          <TextBlock
            design={{
              text: { size: 'medium' },
              box: { width: size.AUTH_BLOCK_WIDTH, height: size.AUTH_BLOCK_HEIGHT },
            }}
            text={email}
          />
          {isRemoved ? (
            <TextBlock
              design={{
                text: { size: 'medium' },
                box: { width: size.AUTH_BLOCK_WIDTH, height: size.AUTH_BLOCK_HEIGHT },
              }}
              text='탈퇴되었습니다.'
            />
          ) : (
            <TextBlock
              text='탈퇴하기'
              design={design.createAuthButtonDesign()}
              click={{ handler: handleSignoutButtonClick }}
            />
          )}
        </>
      )}
    </AuthBox>
  );
};

export default Signout;
